import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  Home,
  ClipboardList,
  ClipboardCheck,
  Archive,
  Bell,
  User,
  Compass,
  ArrowRight,
  ArrowLeft,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui'
import { useTranslation } from '@/hooks'
import { useTour } from '@/hooks/useTour'
import { useAuthContext } from '@/components/auth'
import type { LucideIcon } from 'lucide-react'

const TOUR_ITEMS: Array<{
  tourId: string
  icon: LucideIcon
  nameKey: string
  descKey: string
}> = [
  { tourId: 'dashboard', icon: Home, nameKey: 'nav.dashboard', descKey: 'dashboard.navTour.dashboardDesc' },
  { tourId: 'rules', icon: ClipboardList, nameKey: 'nav.rules', descKey: 'dashboard.navTour.rulesDesc' },
  { tourId: 'rule-matches', icon: ClipboardCheck, nameKey: 'nav.ruleMatches', descKey: 'dashboard.navTour.ruleMatchesDesc' },
  { tourId: 'archive', icon: Archive, nameKey: 'nav.archive', descKey: 'dashboard.navTour.archiveDesc' },
  { tourId: 'notifications', icon: Bell, nameKey: 'dashboard.navTour.notificationsName', descKey: 'dashboard.navTour.notificationsDesc' },
  { tourId: 'profile', icon: User, nameKey: 'dashboard.navTour.profileName', descKey: 'dashboard.navTour.profileDesc' },
]

const TOUR_STEPS = TOUR_ITEMS.map((item) => ({
  id: `nav-${item.tourId}`,
  modal: true,
}))

const CUTOUT_PAD = 4

export function NavigationTour() {
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  const {
    isActive,
    isComplete,
    currentStep,
    totalSteps,
    nextStep,
    previousStep,
    completeTour,
    startTour,
    resetTour,
  } = useTour({
    steps: TOUR_STEPS,
    storageKey: `nav_tour_completed_${user?.id}`,
  })

  const handleEvent = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail
    if (detail?.force) {
      resetTour()
      setTimeout(() => startTour(), 0)
    } else if (!isComplete) {
      startTour()
    }
  }, [isComplete, resetTour, startTour])

  useEffect(() => {
    window.addEventListener('nav-tour', handleEvent)
    return () => window.removeEventListener('nav-tour', handleEvent)
  }, [handleEvent])

  // Track the current step's target element position in the topbar
  useEffect(() => {
    if (!isActive) {
      setTargetRect(null)
      return
    }

    const item = TOUR_ITEMS[currentStep]
    const update = () => {
      const el = document.querySelector(`[data-nav-tour="${item.tourId}"]`)
      if (el) {
        const rect = el.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          setTargetRect(rect)
          return
        }
      }
      setTargetRect(null)
    }

    update()
    const raf = requestAnimationFrame(update)
    window.addEventListener('resize', update)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', update)
    }
  }, [isActive, currentStep])

  if (!isActive) return null

  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === totalSteps - 1
  const activeItem = TOUR_ITEMS[currentStep]

  return createPortal(
    <div className="fixed inset-0 z-modal">
      {/* Click blocker (transparent, blocks all pointer events) */}
      <div className="absolute inset-0" />

      {/* Spotlight cutout — reveals the focused nav item through the overlay */}
      {targetRect ? (
        <div
          className="pointer-events-none"
          style={{
            position: 'fixed',
            top: targetRect.top - CUTOUT_PAD,
            left: targetRect.left - CUTOUT_PAD,
            width: targetRect.width + CUTOUT_PAD * 2,
            height: targetRect.height + CUTOUT_PAD * 2,
            borderRadius: 8,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            zIndex: 1,
            transition: 'top 300ms ease-out, left 300ms ease-out, width 300ms ease-out, height 300ms ease-out',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/50" />
      )}

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4" style={{ zIndex: 2 }}>
        <div
          className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 sm:p-8 animate-scale-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nav-tour-title"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
              <Compass className="w-6 h-6 text-accent" />
            </div>
            <h2 id="nav-tour-title" className="text-h3 text-gray-900">
              {t('dashboard.navTour.title')}
            </h2>
            <p className="text-body-sm text-gray-400 mt-1">
              {t('dashboard.navTour.stepOf', { current: currentStep + 1, total: totalSteps })}
            </p>
          </div>

          {/* Nav items list — current step highlighted */}
          <div className="space-y-2 mb-6">
            {TOUR_ITEMS.map((item, i) => {
              const isCurrent = i === currentStep
              const isPast = i < currentStep

              return (
                <div
                  key={item.nameKey}
                  className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-300 ${
                    isCurrent
                      ? 'bg-accent/10 ring-1 ring-accent/30'
                      : isPast
                        ? 'bg-gray-50 opacity-60'
                        : 'bg-gray-50 opacity-40'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                    isCurrent ? 'bg-accent/20' : 'bg-accent/10'
                  }`}>
                    <item.icon className={`w-5 h-5 transition-colors duration-300 ${
                      isCurrent ? 'text-accent' : 'text-accent/60'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className={`text-body font-medium transition-colors duration-300 ${
                      isCurrent ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {t(item.nameKey)}
                    </h3>
                    {isCurrent && (
                      <p
                        key={currentStep}
                        className="text-body-sm text-gray-600 mt-0.5 animate-fade-in"
                      >
                        {t(activeItem.descKey)}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Navigation footer */}
          <div className="flex items-center justify-between">
            <div>
              {!isFirstStep && (
                <Button variant="secondary" size="md" onClick={previousStep}>
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  {t('common.back')}
                </Button>
              )}
            </div>
            <div>
              {isLastStep ? (
                <Button variant="primary" size="md" onClick={completeTour}>
                  <Check className="w-4 h-4 mr-1.5" />
                  {t('dashboard.navTour.dismiss')}
                </Button>
              ) : (
                <Button variant="primary" size="md" onClick={nextStep}>
                  {t('common.next')}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
